import { createClient, type SupabaseClient } from '@supabase/supabase-js';

// Single Supabase client for the whole SceneForge app.
//
// SCENEFORGE_PLAN.md §3 mandates no cross-subdomain SSO — the session lives in
// this origin's localStorage only. PKCE flow lets the email-link callback land
// on /auth/callback without leaking the access token into the URL fragment.
//
// We intentionally do NOT export the URL or anon key — only the configured
// client — so callers can't accidentally build their own client with the
// wrong auth options.

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  // Fail loudly at import time so a misconfigured deployment surfaces in the
  // first network panel screenshot rather than as a vague auth error later.
  throw new Error(
    '[SCENEFORGE] Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. ' +
      'Check Cloudflare Pages env vars (or .env.local for local dev).',
  );
}

export const supabase: SupabaseClient = createClient(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      flowType: 'pkce',
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      storageKey: 'sceneforge-auth',
    },
  },
);

/** Return the current session's JWT, or null if not signed in. */
export async function getAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}
